import React, { useState, useEffect } from "react";
import {
    CreditCard,
    Calendar,
    User,
    BookOpen,
    Building,
    Download,
    CheckCircle,
    Clock,
    Search
} from "lucide-react";
import "./MasterFees.css";
import { fetchParentInvoices, downloadInvoice, downloadReceipt } from "../../services/invoiceService";
import { createPaymentOrder, verifyPayment } from "../../services/paymentService";
import PaymentModal from "../../components/PaymentModal/PaymentModal";

const MasterFees = () => {
    const [invoices, setInvoices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingAction, setLoadingAction] = useState({ id: null, type: null });
    const [searchTerm, setSearchTerm] = useState("");
    const [popup, setPopup] = useState({ show: false, title: "", message: "", type: "success" });
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [pdfConfig, setPdfConfig] = useState({ show: false, data: null, title: "" });
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    useEffect(() => {
        loadInvoices();
    }, []);

    const loadInvoices = async () => {
        try {
            setIsLoading(true);
            const data = await fetchParentInvoices();
            setInvoices(data || []);
        } catch (err) {
            console.error("Failed to load invoices", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewDocument = async (inv, type) => {
        try {
            setLoadingAction({ id: inv.InvoiceId, type });
            const res = type === "invoice"
                ? await downloadInvoice(inv.InvoiceId)
                : await downloadReceipt(inv.InvoiceId);

            if (res && res.file) {
                // Convert base64 to Blob URL for better mobile compatibility
                const byteCharacters = atob(res.file);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(blob);

                // Detect mobile to decide between modal and direct opening
                const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

                if (isMobile) {
                    // For mobile, directly opening in a new tab is much more reliable
                    const newWindow = window.open(blobUrl, '_blank');
                    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                        // Pop-up blocked fallback
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.target = '_blank';
                        link.click();
                    }
                } else {
                    setPdfConfig({
                        show: true,
                        data: blobUrl,
                        title: type === "invoice" ? `Invoice #${inv.InvoiceNumber}` : `Receipt #${inv.InvoiceNumber}`
                    });
                }
            }
        } catch (err) {
            console.error("Failed to load document", err);
            setPopup({
                show: true,
                title: "Load Error",
                message: typeof err === "string" && err ? err : "Could not retrieve the document. Please try again.",
                type: "error"
            });
        } finally {
            setLoadingAction({ id: null, type: null });
        }
    };

    const handlePayNow = async (inv) => {
        try {
            setLoadingAction({ id: inv.InvoiceId, type: 'pay' });
            const payload = {
                invoiceId: parseInt(inv.InvoiceId),
                groupId: parseInt(inv.GroupId)
            };

            const orderData = await createPaymentOrder(payload);


            // INITIALIZE RAZORPAY
            const options = {
                "key": orderData.key,
                "amount": orderData.amount * 100,
                "currency": "INR",
                "name": "Feezaap",
                "description": `Invoice Payment - ${inv.InvoiceNumber}`,
                "order_id": orderData.orderId,
                "handler": async function (response) {
                    try {
                        setIsLoading(true);
                        const verifyPayload = {
                            invoiceId: parseInt(inv.InvoiceId),
                            groupId: parseInt(inv.GroupId),
                            razorpayOrderId: response.razorpay_order_id,
                            razorpayPaymentId: response.razorpay_payment_id,
                            razorpaySignature: response.razorpay_signature
                        };

                        await verifyPayment(verifyPayload);

                        setPopup({
                            show: true,
                            title: "Payment Successful",
                            message: "Your payment has been verified and processed successfully!",
                            type: "success"
                        });
                        loadInvoices();
                    } catch (err) {
                        console.error("Verification failed", err);
                        setPopup({
                            show: true,
                            title: "Verification Failed",
                            message: "Payment was successful but verification failed. Please contact support.",
                            type: "error"
                        });
                    } finally {
                        setIsLoading(false);
                    }
                },
                "modal": {
                    "ondismiss": function () {
                        console.log("Payment popup closed");
                    }
                },
                "prefill": {
                    "name": inv.StudentName || "Parent",
                    "email": inv.Email || "",
                    "contact": inv.PhoneNumber || ""
                },
                "theme": {
                    "color": "#16a34a"
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();

        } catch (err) {
            console.error("Payment failed", err);
            setPopup({
                show: true,
                title: "Payment Error",
                message: typeof err === "string" && err ? err : "Failed to initialize payment. Please try again.",
                type: "error"
            });
        } finally {
            setLoadingAction({ id: null, type: null });
        }
    };

    const filteredInvoices = invoices
        .filter(inv => {
            const matchesSearch = 
                inv.InvoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                inv.StudentName?.toLowerCase().includes(searchTerm.toLowerCase());
            
            // Determine the definitive status of the invoice
            const statusVal = (inv.Status || "").toUpperCase();
            const payStatusVal = (inv.PaymentStatus || "").toUpperCase();
            const isPaid = statusVal === "PAID" || statusVal === "COLLECTED" || statusVal === "SUCCESS" || 
                           payStatusVal === "PAID" || payStatusVal === "COLLECTED" || payStatusVal === "SUCCESS";
            const isPendingApproval = statusVal === "PENDING_APPROVAL" || payStatusVal === "PENDING_APPROVAL";
            const isPending = statusVal === "PENDING" || payStatusVal === "PENDING" || isPendingApproval;

            let matchesStatus = false;
            if (statusFilter === "ALL") {
                matchesStatus = true;
            } else if (statusFilter === "PENDING") {
                matchesStatus = isPending && !isPaid;
            } else if (statusFilter === "PAID") {
                matchesStatus = isPaid;
            }
            
            return matchesSearch && matchesStatus;
        })
        .sort((a, b) => {
            if (a.Status === "PENDING" && b.Status !== "PENDING") return -1;
            if (a.Status !== "PENDING" && b.Status === "PENDING") return 1;
            return 0;
        });

    return (
        <div className="fees-page-container">
            {/* HEADER */}
            <div className="fees-header">
                <div className="fees-search">
                    <Search size={18} />
                    <input 
                        type="text" 
                        placeholder="Search by invoice #, student name, or parent..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="fees-filters">
                    <button 
                        className={`filter-btn ${statusFilter === 'ALL' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('ALL')}
                    >
                        All
                    </button>
                    <button 
                        className={`filter-btn pending ${statusFilter === 'PENDING' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('PENDING')}
                    >
                        <Clock size={14} />
                        Pending
                    </button>
                    <button 
                        className={`filter-btn paid ${statusFilter === 'PAID' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('PAID')}
                    >
                        <CheckCircle size={14} />
                        Paid
                    </button>
                </div>
            </div>

            <div className="fees-content-area">
                {isLoading && (
                    <div className="fees-loader">
                        <div className="spinner"></div>
                        <p>Loading Invoices...</p>
                    </div>
                )}

                {!isLoading && filteredInvoices.length === 0 && (
                    <div className="empty-state">
                        <CreditCard size={48} />
                        <h3>No Invoices Found</h3>
                        <p>There are no fee records available at this moment.</p>
                    </div>
                )}

                <div className="invoice-cards-grid">
                    {filteredInvoices.map((inv, index) => {
                        const statusVal = (inv.Status || "").toUpperCase();
                        const payStatusVal = (inv.PaymentStatus || "").toUpperCase();
                        const isPaid = statusVal === "PAID" || statusVal === "COLLECTED" || statusVal === "SUCCESS" || 
                                       payStatusVal === "PAID" || payStatusVal === "COLLECTED" || payStatusVal === "SUCCESS";
                        const isPendingApproval = statusVal === "PENDING_APPROVAL" || payStatusVal === "PENDING_APPROVAL";

                        return (
                            <div className="invoice-premium-card" key={`${inv.InvoiceId}-${index}`}>
                            {/* TOP STRIP */}
                            <div className="card-top-strip">
                                <div className="inv-badge">
                                    <FileText size={14} />
                                    <span>{inv.InvoiceNumber}</span>
                                </div>
                                <div className={`status-pill ${isPaid ? 'paid' : (isPendingApproval ? 'pending' : 'pending')}`}>
                                    {isPaid ? <CheckCircle size={14} /> : <Clock size={14} />}
                                    <span>{isPaid ? "PAID" : (isPendingApproval ? "WAITING APPROVAL" : "PENDING")}</span>
                                </div>
                            </div>

                            {/* BODY */}
                            <div className="card-body-content">
                                <div className="student-profile-row">
                                    <div className="logo-wrap">
                                        <img src={inv.LogoUrl} alt="Logo" />
                                    </div>
                                    <div className="names-wrap">
                                        <h3>{inv.StudentName}</h3>
                                        <div className="parent-tag">Parent: {inv.ParentName}</div>
                                    </div>
                                </div>

                                <div className="details-grid">
                                    <div className="detail-item">
                                        <Building size={14} />
                                        <span>{inv.GroupName}</span>
                                    </div>
                                    <div className="detail-item">
                                        <MapPin size={14} />
                                        <span>{inv.BranchName}</span>
                                    </div>
                                    <div className="detail-item">
                                        <BookOpen size={14} />
                                        <span>{inv.CourseName}</span>
                                    </div>
                                    <div className="detail-item">
                                        <Calendar size={14} />
                                        <span>Due: {new Date(inv.DueDate).toLocaleDateString()}</span>
                                    </div>
                                </div>

                                <div className="amount-section">
                                    <div className="amount-main">
                                        <span className="currency">₹</span>
                                        <span className="value">{parseFloat(inv.Amount).toLocaleString()}</span>
                                    </div>
                                    <div className="tax-info">
                                        Incl. {inv.TaxPercentage}% GST
                                    </div>
                                </div>
                            </div>

                            {/* FOOTER ACTIONS */}
                            <div className="card-footer">
                                {!isPaid ? (
                                    <>
                                        <button
                                            className="view-btn"
                                            onClick={() => handleViewDocument(inv, "invoice")}
                                            disabled={loadingAction.id === inv.InvoiceId}
                                        >
                                            {loadingAction.id === inv.InvoiceId && loadingAction.type === 'invoice' ? (
                                                <div className="btn-spinner"></div>
                                            ) : (
                                                <>
                                                    <Download size={16} />
                                                    Download Invoice
                                                </>
                                            )}
                                        </button>
                                        {!isPendingApproval && (
                                            <button
                                                className="mark-paid-btn"
                                                onClick={() => {
                                                    setSelectedInvoice(inv);
                                                    setShowPaymentModal(true);
                                                }}
                                            >
                                                <CheckCircle size={16} />
                                                Mark as Paid
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <button
                                        className="view-btn wide"
                                        onClick={() => handleViewDocument(inv, "receipt")}
                                        disabled={loadingAction.id === inv.InvoiceId}
                                    >
                                        {loadingAction.id === inv.InvoiceId && loadingAction.type === 'receipt' ? (
                                            <div className="btn-spinner"></div>
                                        ) : (
                                            <>
                                                <CheckCircle size={16} />
                                                View Receipt
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>

            {/* PDF PREVIEW MODAL */}
            {pdfConfig.show && (
                <div className="pdf-modal-overlay">
                    <div className="pdf-modal-container">
                        <div className="pdf-modal-header">
                            <h3>{pdfConfig.title}</h3>
                            <button 
                                className="pdf-close-btn" 
                                onClick={() => {
                                    if (pdfConfig.data) URL.revokeObjectURL(pdfConfig.data);
                                    setPdfConfig({ ...pdfConfig, show: false, data: null });
                                }}
                            >
                                <X size={20} />
                                <span>Close</span>
                            </button>
                        </div>
                        <div className="pdf-viewer-wrap">
                            <iframe
                                src={pdfConfig.data}
                                title="Document Preview"
                                frameBorder="0"
                            ></iframe>
                        </div>
                    </div>
                </div>
            )}

            {/* POPUP OVERLAY */}
            {popup.show && (
                <div className="popup-overlay" onClick={() => setPopup({ ...popup, show: false })}>
                    <div className="popup-card" onClick={e => e.stopPropagation()}>
                        <div className={`popup-icon ${popup.type}`}>
                            {popup.type === "success" ? "✓" : "!"}
                        </div>
                        <h2>{popup.title}</h2>
                        <p>{popup.message}</p>
                        <button className="popup-btn" onClick={() => setPopup({ ...popup, show: false })}>
                            Got it
                        </button>
                    </div>
                </div>
            )}

            {showPaymentModal && selectedInvoice && (
                <PaymentModal
                    invoice={selectedInvoice}
                    onClose={() => {
                        setShowPaymentModal(false);
                        setSelectedInvoice(null);
                    }}
                    onSuccess={(res) => {
                        if (res && res.success) {
                            setInvoices(prev => prev.map(inv => 
                                inv.InvoiceId === selectedInvoice.InvoiceId 
                                    ? { ...inv, Status: "PENDING_APPROVAL", PaymentStatus: "PENDING_APPROVAL" } 
                                    : inv
                            ));
                        }
                    }}
                />
            )}
        </div>
    );
};

// Re-defining some icons that might be missing from standard imports if not careful
const FileText = ({ size }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" /></svg>;
const MapPin = ({ size }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>;
const IndianRupee = ({ size }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h12" /><path d="M6 8h12" /><path d="m6 13 8.5 8" /><path d="M6 13h3" /><path d="M9 13c6.667 0 6.667-10 0-10" /></svg>;
const Eye = ({ size }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>;
const X = ({ size }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>;

export default MasterFees;
